/**
 * Codec selection for MediaRecorder.
 *
 * Screen: VP9 first: it is roughly 30% smaller than VP8 at the same quality on
 * screen content, which is what we are almost always recording. VP8 is the
 * fallback for older Chromium and Firefox builds. Safari's MediaRecorder emits
 * MP4/AAC and does not support WebM at all, so it gets its own branch — the
 * API accepts video/mp4 as an upload content type, so nothing downstream
 * cares.
 *
 * Audio: Opus in WebM, for the same reason everyone else picks it — it is the
 * best speech codec at low bitrates and every non-Safari browser records it.
 * Safari again lands on MP4/AAC. The API maps the content type to a `kind` of
 * 'audio' by itself, so choosing the mime type here is the whole of what makes
 * a voice note a voice note.
 */

/** Which capture path the recorder is on. */
export type RecordingMode = 'screen' | 'audio';

/** The upload content types slate-api accepts. Anything else is a 400. */
export type UploadContentType =
  | 'video/webm'
  | 'video/mp4'
  | 'audio/webm'
  | 'audio/mp4';

export interface CodecChoice {
  /** Passed to MediaRecorder. Empty means "let the browser decide". */
  mimeType: string;
  /** Passed to POST /v1/videos — the API only knows base types. */
  contentType: UploadContentType;
  /** Shown in the recorder's diagnostics line. */
  label: string;
}

const SCREEN_CANDIDATES: CodecChoice[] = [
  { mimeType: 'video/webm;codecs=vp9,opus', contentType: 'video/webm', label: 'WebM · VP9 / Opus' },
  { mimeType: 'video/webm;codecs=vp8,opus', contentType: 'video/webm', label: 'WebM · VP8 / Opus' },
  { mimeType: 'video/webm', contentType: 'video/webm', label: 'WebM · browser default' },
  { mimeType: 'video/mp4;codecs=avc1,mp4a.40.2', contentType: 'video/mp4', label: 'MP4 · H.264 / AAC' },
  { mimeType: 'video/mp4', contentType: 'video/mp4', label: 'MP4 · browser default' },
];

const AUDIO_CANDIDATES: CodecChoice[] = [
  { mimeType: 'audio/webm;codecs=opus', contentType: 'audio/webm', label: 'WebM · Opus' },
  { mimeType: 'audio/webm', contentType: 'audio/webm', label: 'WebM · browser default' },
  { mimeType: 'audio/mp4;codecs=mp4a.40.2', contentType: 'audio/mp4', label: 'MP4 · AAC' },
  { mimeType: 'audio/mp4', contentType: 'audio/mp4', label: 'MP4 · browser default' },
];

/** The last resort: MediaRecorder exists but claims to support nothing. */
const LAST_RESORT: Record<RecordingMode, CodecChoice> = {
  screen: { mimeType: '', contentType: 'video/webm', label: 'browser default' },
  audio: { mimeType: '', contentType: 'audio/webm', label: 'browser default' },
};

export function pickCodec(mode: RecordingMode = 'screen'): CodecChoice | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of mode === 'audio' ? AUDIO_CANDIDATES : SCREEN_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) return candidate;
  }
  // Some browsers report nothing as supported but still record with the
  // default. Only claim this when MediaRecorder exists at all.
  return LAST_RESORT[mode];
}

/** Bitrates. Speech does not need what screen video needs — 96k is plenty. */
export const AUDIO_ONLY_BITRATE = 96_000;
/** The audio leg of a screen recording, which also carries system audio. */
export const SCREEN_AUDIO_BITRATE = 128_000;

const EXTENSIONS: Record<UploadContentType, string> = {
  'video/webm': 'webm',
  'video/mp4': 'mp4',
  'audio/webm': 'weba',
  'audio/mp4': 'm4a',
};

/** File extension for the "download this take" link on the preview screen. */
export function extensionFor(contentType: UploadContentType): string {
  return EXTENSIONS[contentType];
}

/** Does this browser have everything the recorder needs? */
export interface CaptureSupport {
  displayCapture: boolean;
  userMedia: boolean;
  mediaRecorder: boolean;
  canvasCapture: boolean;
  /** True when a screen can be recorded. */
  screenOk: boolean;
  /** True when an audio note can be recorded. Phones can do this; not screens. */
  audioOk: boolean;
  /** True when we can record at all, either way. */
  ok: boolean;
}

export function detectSupport(): CaptureSupport {
  const displayCapture =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  const userMedia =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function';
  const mediaRecorder = typeof MediaRecorder !== 'undefined';
  const canvasCapture =
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function';

  const screenOk = displayCapture && mediaRecorder;
  const audioOk = userMedia && mediaRecorder;

  return {
    displayCapture,
    userMedia,
    mediaRecorder,
    canvasCapture,
    screenOk,
    audioOk,
    ok: screenOk || audioOk,
  };
}
